/**
 * Browser canvas helpers for oversized image uploads.
 * Pure file-in / blob-out — Dropzone, preview, and save wiring stay in callers.
 */

export const IMAGE_TOO_LARGE = 'IMAGE_TOO_LARGE';

const DEFAULT_EDGE_STEPS = [2048, 1600, 1280, 1024, 800, 640, 480, 320];
const DEFAULT_QUALITY_STEPS = [
  0.92, 0.85, 0.78, 0.7, 0.62, 0.54, 0.46, 0.38, 0.3, 0.22, 0.14, 0.06,
];

export const asUploadFile = (blob, fallbackName, fallbackType) => {
  if (blob instanceof File && blob.name) return blob;
  return new File([blob], fallbackName || 'image.jpg', {
    type: (blob && blob.type) || fallbackType || 'image/jpeg',
    lastModified: Date.now(),
  });
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Image encoding failed.'));
      },
      type,
      quality
    );
  });

/**
 * PNG toBlob ignores quality. Only keep the PNG shrink path when the
 * bitmap actually has a non-opaque pixel. Check a copy no larger than
 * 2048px so a huge upload does not allocate a full-size pixel buffer.
 */
const bitmapHasTransparency = (bitmap) => {
  const width = bitmap && bitmap.width;
  const height = bitmap && bitmap.height;
  if (!width || !height) return false;

  const longest = Math.max(width, height);
  const scale = Math.min(1, 2048 / longest);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return true;

  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  let pixels;
  try {
    pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  } catch (err) {
    return true;
  }
  for (let i = 3; i < pixels.length; i += 4) {
    if (pixels[i] !== 255) return true;
  }
  return false;
};

/**
 * Canvas compress for one oversized image.
 * Photos (jpeg, webp, and opaque png) step quality from 0.92 downward,
 * then shrink the longest edge if quality alone cannot reach the limit.
 * PNG logos with transparency shrink the longest edge instead.
 * preserveDimensions keeps the source pixel size (header and banner).
 * Returns null when stillCurrent() reports a newer drop superseded this one.
 */
export const compressImageOnCanvas = async (file, maxBytes, options = {}) => {
  const stillCurrent = options.stillCurrent || (() => true);
  const preserveDimensions = !!options.preserveDimensions;
  const baseMime = ((file.type || '').split(';')[0] || '')
    .trim()
    .toLowerCase();
  const fileName = (file.name || '').toLowerCase();

  if (
    baseMime === 'image/gif' ||
    baseMime === 'image/svg+xml' ||
    fileName.endsWith('.gif') ||
    fileName.endsWith('.svg')
  ) {
    throw new Error(IMAGE_TOO_LARGE);
  }

  const isPng = baseMime === 'image/png' || fileName.endsWith('.png');
  const outputType =
    baseMime === 'image/webp' || fileName.endsWith('.webp')
      ? 'image/webp'
      : 'image/jpeg';

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
    if (!stillCurrent()) return null;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas is not supported.');

    const longestSide = Math.max(bitmap.width, bitmap.height) || 1;
    const draw = (maxEdge) => {
      const scale = preserveDimensions
        ? 1
        : Math.min(1, maxEdge / longestSide);
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    };

    const keepPng = isPng && bitmapHasTransparency(bitmap);
    if (!stillCurrent()) return null;

    if (keepPng) {
      const edges = preserveDimensions ? [longestSide] : DEFAULT_EDGE_STEPS;
      let previousSize = '';
      for (const maxEdge of edges) {
        if (!stillCurrent()) return null;
        draw(maxEdge);
        const drawnSize = canvas.width + 'x' + canvas.height;
        if (drawnSize === previousSize) continue;
        previousSize = drawnSize;
        const blob = await canvasToBlob(canvas, 'image/png');
        if (blob.size <= maxBytes) return blob;
        if (preserveDimensions) break;
      }
      throw new Error(IMAGE_TOO_LARGE);
    }

    const edges = preserveDimensions ? [longestSide] : DEFAULT_EDGE_STEPS;
    let previousSize = '';
    for (const maxEdge of edges) {
      if (!stillCurrent()) return null;
      draw(maxEdge);
      const drawnSize = canvas.width + 'x' + canvas.height;
      if (drawnSize === previousSize) continue;
      previousSize = drawnSize;
      for (const quality of DEFAULT_QUALITY_STEPS) {
        if (!stillCurrent()) return null;
        const blob = await canvasToBlob(canvas, outputType, quality);
        if (blob.size <= maxBytes) return blob;
      }
      if (preserveDimensions) break;
    }
    throw new Error(IMAGE_TOO_LARGE);
  } finally {
    if (bitmap) bitmap.close();
  }
};



// WEBPACK FOOTER //
// ./src/utils/imageUpload.js